/**
 * /app/$tenantSlug/accounting/sales — Books → Sales → Invoices (G_ACC5).
 *
 * Sales has two top-bar tabs like the other Books modules (lib/surfaces.ts
 * "run:books-sales"): Invoices (this page) and Quotes
 * (/accounting/sales/quotes). The old in-page tab is gone; an old
 * ?tab=quotes link (emails, bookmarks) redirects to the Quotes page.
 *
 * Migration note (Phase 3e — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/accounting/sales/page.tsx.
 */

import { createFileRoute, Link, redirect, useParams } from "@tanstack/react-router";
import { z } from "zod";
import { Plus } from "lucide-react";
import { AccountingModuleShell } from "@/components/shared/accounting-module-shell";
import { InvoiceList } from "@/components/accounting/invoice-list";
import { Button } from "@/components/ui/button";
import { accountingKeys } from "@/hooks/use-accounting";
import { accountingApi } from "@/lib/api";

const salesSearchSchema = z.object({
  // Old links: ?tab=invoices stays here, ?tab=quotes goes to Quotes.
  tab: z.enum(["invoices", "quotes"]).optional().catch(undefined),
});

export const Route = createFileRoute("/_authgated/app/$tenantSlug/accounting/sales/")({
  validateSearch: salesSearchSchema,
  beforeLoad: ({ params, search }) => {
    if (search.tab === "quotes") {
      throw redirect({
        to: "/app/$tenantSlug/accounting/sales/quotes",
        params: { tenantSlug: params.tenantSlug },
        replace: true,
      });
    }
  },
  // A9 hover-prefetch: the unfiltered list InvoiceList starts with.
  loader: ({ context }) =>
    context.queryClient
      .ensureQueryData({
        queryKey: accountingKeys.invoices(undefined),
        queryFn: ({ signal }) => accountingApi.listInvoices(undefined, signal),
      })
      .catch(() => undefined),
  component: SalesInvoicesRoute,
});

function SalesInvoicesRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  return (
    <AccountingModuleShell
      title="Invoices"
      description="Invoices for your customers."
      actions={
        <Button asChild className="gap-1.5">
          <Link to="/app/$tenantSlug/accounting/sales/invoices/new" params={{ tenantSlug }}>
            <Plus aria-hidden className="h-4 w-4" />
            New Invoice
          </Link>
        </Button>
      }
    >
      <InvoiceList />
    </AccountingModuleShell>
  );
}
